import { Device, type Call } from '@twilio/voice-sdk'
import { config, endpoint } from './config'
import { getSupabase } from './supabase'
import { decodeIncomingContactId, type VoiceHistoryCall, type VoiceSession } from './callWorkspace'

export type VoiceState = 'ready' | 'connecting' | 'dialing' | 'ringing' | 'connected' | 'ended' | 'failed'
export type VoiceDiagnostics = {
  deviceExists: boolean
  deviceState: string
  identity: string | null
  tokenExpiresAt: string | null
  tokenExpired: boolean | null
  incomingListenerAttached: boolean
  signalingActive: boolean
  edge: string | null
  lastTokenInstalledAt: string | null
  lastRegisteredAt: string | null
  incomingFrom: string | null
  incomingCallPending: boolean
  disposed: boolean
  lastRegistrationErrorCode: number | null
}

export type VoiceWorkspaceSnapshot = { calls: VoiceHistoryCall[]; actors: Record<string, string> }
export type VoiceCallAttempt = { context: string; attemptId: string; contactId: string | null; phoneE164: string; resolutionState: 'UNIQUE_MATCH'|'UNMATCHED'|'AMBIGUOUS' }

type VoiceConnectOptions = { params: { context: string } }
const authHeaders = async () => {
  const client = getSupabase()
  const { data } = await client?.auth.getSession() || { data: { session: null } }
  const token = data.session?.access_token
  if (!token || !config.workspaceId) throw new Error('crm_authentication_required')
  return { authorization: `Bearer ${token}`, 'x-crm-workspace-id': config.workspaceId, 'content-type': 'application/json' }
}
const request = async <T,>(path: string, body?: Record<string, unknown>, signal?:AbortSignal) => {
  const headers=await authHeaders()
  if(signal?.aborted)throw new Error('voice_read_scope_retired')
  const response = await fetch(endpoint(path), { method: 'POST', headers, body: body ? JSON.stringify(body) : undefined, signal })
  const value = await response.json().catch(() => null)
  if (!response.ok) {
    if(value?.reason==='IMPORTED_PHONE_VERIFICATION_REQUIRED')throw Object.assign(new Error('Verify this imported phone number with independent source evidence before calling.'),{code:value.reason})
    throw new Error(value?.reason || value?.error || `request_failed_${response.status}`)
  }
  return value as T
}
export const getVoiceWorkspace = (signal?:AbortSignal) => request<VoiceWorkspaceSnapshot>(config.endpoints.voiceWorkspace, {}, signal)
export const getVoiceCallability = (contactId:string) => request<{ok:true;callable:boolean;reason:string;phoneE164?:string;recommendedCallAfter?:string|null;recipientTimezone?:string|null;earlyCall?:boolean;restUntil?:string|null;returnIntro?:string|null}>(config.endpoints.voiceCallability,{contactId})
export const attachVoiceCall = (input: { attemptId: string; contactId?: string | null; organizationId?: string | null }) => request<{ ok: true }>(config.endpoints.voiceCallAttach, input)
const utf8Bytes = (value: string) => new Blob([value]).size
export const voiceConnectOptions = (context: unknown): VoiceConnectOptions => {
  if (typeof context !== 'string' || !context || /\s/.test(context)) throw new Error('invalid_voice_call_context')
  const params = new URLSearchParams({ context }).toString()
  if (utf8Bytes(params) > 800) throw new Error('voice_call_context_too_large')
  return { params: { context } }
}

export class CrmVoiceClient {
  private device: Device | null = null
  private calls = new Map<string, Call>()
  private activeCallId: string | null = null
  private sessions = new Map<string, VoiceSession>()
  private registration: Promise<void> | null = null
  private tokenRefresh: Promise<void> | null = null
  private identity: string | null = null
  private statusTimer: ReturnType<typeof setInterval> | null = null
  private disposed = false
  private generation = 0
  private incomingListenerAttached = false
  private tokenExpiresAt: number | null = null
  private lastTokenInstalledAt: string | null = null
  private lastRegisteredAt: string | null = null
  private incomingFrom: string | null = null
  private incomingCallPending = false
  private outboundAttempt: Promise<VoiceCallAttempt> | null = null
  private outboundTarget: string | null = null
  private lastRegistrationErrorCode: number | null = null

  constructor(private readonly setState: (state: VoiceState) => void, private readonly setSessions: (sessions: VoiceSession[]) => void = () => undefined) {}

  private callId(call: Call) { return call.parameters.CallSid || crypto.randomUUID() }
  private emitSessions() { this.setSessions([...this.sessions.values()].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt))) }
  private updateSession(id: string, next: Partial<VoiceSession>) {
    const current = this.sessions.get(id)
    if (!current) return
    this.sessions.set(id, { ...current, ...next })
    this.emitSessions()
  }
  private removeCall(id: string, ended = true) {
    const current = this.sessions.get(id)
    if (current && ended) {
      const endedAt = new Date().toISOString()
      const durationSeconds = current.connectedAt ? Math.max(current.durationSeconds, Math.floor((Date.parse(endedAt) - Date.parse(current.connectedAt)) / 1000)) : current.durationSeconds
      this.updateSession(id, { state: 'ended', endedAt, durationSeconds, muted: false })
    }
    this.calls.delete(id)
    if (this.activeCallId === id) this.activeCallId = null
    this.incomingFrom = [...this.sessions.values()].find(session => session.state === 'ringing')?.phone || null
    this.incomingCallPending = [...this.sessions.values()].some(session => session.state === 'ringing')
  }

  private readTokenExpiration(token: string) {
    try {
      const encodedPayload = token.split('.')[1]
      if (!encodedPayload) return null
      const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/')
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
      const payload = JSON.parse(atob(padded)) as { exp?: unknown }
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null
    } catch {
      return null
    }
  }

  private installTokenMetadata(token: string) {
    this.tokenExpiresAt = this.readTokenExpiration(token)
    this.lastTokenInstalledAt = new Date().toISOString()
  }

  private async refreshDeviceToken(device: Device) {
    if (this.disposed || device !== this.device) return
    if (this.tokenRefresh) return this.tokenRefresh
    const generation = this.generation
    this.tokenRefresh = (async () => {
      const next = await request<{ token: string; identity: string }>(config.endpoints.voiceToken)
      if (this.disposed || generation !== this.generation || device !== this.device) return
      if (!this.identity || next.identity !== this.identity) throw new Error('voice_identity_changed')
      this.installTokenMetadata(next.token)
      device.updateToken(next.token)
      if (device.state === 'unregistered') await device.register()
      if (device.state === 'registered' && !this.activeCallId && !this.incomingCallPending) this.setState('ready')
    })().finally(() => { this.tokenRefresh = null })
    return this.tokenRefresh
  }

  private attachDevice(device: Device) {
    this.incomingListenerAttached = true
    device.on('registered', () => {
      if (device !== this.device || this.disposed) return
      this.lastRegisteredAt = new Date().toISOString()
      this.lastRegistrationErrorCode = null
      if (!this.activeCallId && !this.outboundAttempt && !this.incomingCallPending) this.setState('ready')
    })
    device.on('error', error => {
      if (device !== this.device || this.disposed) return
      if (error.code === 20104) {
        void this.refreshDeviceToken(device).catch(() => this.setState('failed'))
        return
      }
      this.lastRegistrationErrorCode = Number.isInteger(error.code) ? error.code : null
      // A rejected token cannot recover through transport reconnects. Explicit
      // retry obtains a fresh authenticated token after the binding is repaired.
      if (error.code === 20101) {
        this.device = null
        this.incomingListenerAttached = false
        device.destroy()
      }
      this.setState('failed')
    })
    device.on('tokenWillExpire', () => void this.refreshDeviceToken(device).catch(() => this.setState('failed')))
    device.on('incoming', call => {
      if (device !== this.device || this.disposed || this.outboundAttempt) {
        call.reject()
        return
      }
      const id = this.callId(call)
      const from = call.parameters.From || ''
      const contactId = decodeIncomingContactId(call.customParameters.get('context'), from)
      this.calls.set(id, call)
      this.sessions.set(id, { id, direction: 'INBOUND', state: 'ringing', phone: from, contactId, startedAt: new Date().toISOString(), connectedAt: null, endedAt: null, durationSeconds: 0, muted: false })
      this.emitSessions()
      this.incomingFrom = from || null
      this.incomingCallPending = true
      if (!this.activeCallId) this.setState('ringing')
      call.on('accept', () => { this.activeCallId = id; this.updateSession(id, { state: 'connected', connectedAt: new Date().toISOString() }); this.incomingCallPending = [...this.sessions.values()].some(session => session.id !== id && session.state === 'ringing'); this.setState('connected') })
      call.on('disconnect', () => { this.removeCall(id); this.setState(this.activeCallId ? 'connected' : this.incomingCallPending ? 'ringing' : 'ended') })
      call.on('cancel', () => { this.removeCall(id); this.setState(this.activeCallId ? 'connected' : this.incomingCallPending ? 'ringing' : 'ended') })
      call.on('reject', () => { this.removeCall(id); this.setState(this.activeCallId ? 'connected' : this.incomingCallPending ? 'ringing' : 'ended') })
    })
  }

  async register() {
    if (this.disposed) throw new Error('voice_client_disposed')
    if (this.device?.state === 'registered') {
      if (!this.activeCallId && !this.outboundAttempt && !this.incomingCallPending) this.setState('ready')
      return
    }
    if (this.registration) return this.registration
    const generation = ++this.generation
    this.registration = (async () => {
      const { token, identity } = await request<{ token: string; identity: string }>(config.endpoints.voiceToken)
      if (this.disposed || generation !== this.generation) return
      this.device?.destroy()
      this.incomingListenerAttached = false
      this.identity = identity
      this.installTokenMetadata(token)
      const device = new Device(token, { closeProtection: false, enableImprovedSignalingErrorPrecision: true, tokenRefreshMs: 120000 })
      this.device = device
      this.attachDevice(device)
      await device.register()
      if (this.disposed || generation !== this.generation || device !== this.device) device.destroy()
    })().finally(() => { this.registration = null })
    return this.registration
  }

  private async refreshStatus(attemptId: string,sessionId:string) {
    try {
      const result = await request<{ call: null | { status: string } }>(config.endpoints.voiceCallStatus, { attemptId })
      if (this.disposed || this.activeCallId !== sessionId) return
      const status = result.call?.status
      if (status === 'ringing' && !this.sessions.get(sessionId)?.connectedAt) this.setState('ringing')
      else if (status === 'in-progress') { const current=this.sessions.get(sessionId);this.updateSession(sessionId,{state:'connected',connectedAt:current?.connectedAt||new Date().toISOString()});this.setState('connected') }
      else if (['completed', 'busy', 'failed', 'no-answer', 'canceled'].includes(status || '')) {
        this.clearStatusPolling()
        const call = this.calls.get(sessionId)
        this.removeCall(sessionId)
        call?.disconnect()
        this.setState('ended')
      }
    } catch { /* transient status polling failures must not change call state */ }
  }

  private startStatusPolling(attemptId: string,sessionId:string) {
    this.clearStatusPolling()
    void this.refreshStatus(attemptId,sessionId)
    this.statusTimer = setInterval(() => void this.refreshStatus(attemptId,sessionId), 1000)
  }

  private clearStatusPolling() {
    if (this.statusTimer) {
      clearInterval(this.statusTimer)
      this.statusTimer = null
    }
  }

  private async connectAttempt(path:string,body:Record<string,unknown>):Promise<VoiceCallAttempt>{
    const target = JSON.stringify([path, body])
    if(this.outboundAttempt){
      if(this.outboundTarget===target)return this.outboundAttempt
      throw new Error('voice_call_in_progress')
    }
    if(this.activeCallId || this.incomingCallPending)throw new Error('voice_call_in_progress')
    this.outboundTarget=target
    this.outboundAttempt=(async()=>{
    if(this.disposed)throw new Error('voice_client_disposed')
    this.setState('connecting')
    const [, callContext] = await Promise.all([this.register(), request<VoiceCallAttempt>(path, body)])
    const connectOptions = voiceConnectOptions(callContext.context)
    const device = this.device
    if (!device) throw new Error('voice_device_unavailable')
    if(this.disposed)throw new Error('voice_client_disposed')
    this.setState('dialing')
    const call = await device.connect(connectOptions)
    if(this.disposed||device!==this.device){call.disconnect();throw new Error('voice_client_disposed')}
    const id = this.callId(call)
    this.calls.set(id, call)
    this.activeCallId = id
    this.sessions.set(id, { id, direction: 'OUTBOUND', state: 'ringing', phone: callContext.phoneE164, contactId:callContext.contactId, startedAt: new Date().toISOString(), connectedAt: null, endedAt: null, durationSeconds: 0, muted: false })
    this.emitSessions()
    this.incomingFrom = null
    this.incomingCallPending = false
    this.startStatusPolling(callContext.attemptId,id)
    call.on('ringing', () => {if(this.activeCallId===id&&!this.sessions.get(id)?.connectedAt)this.setState('ringing')})
    // SDK acceptance opens the browser leg. Only correlated provider status
    // confirms that the destination answered.
    call.on('accept', () => {if(this.activeCallId===id&&!this.sessions.get(id)?.connectedAt)this.setState('dialing')})
    const finish = (failed = false) => {
      const active = this.activeCallId === id
      if(active)this.clearStatusPolling()
      this.removeCall(id)
      if(failed)call.disconnect()
      if(active)this.setState(failed?'failed':this.incomingCallPending?'ringing':'ended')
    }
    call.on('disconnect', () => finish())
    call.on('cancel', () => finish())
    call.on('reject', () => finish())
    call.on('error', () => finish(true))
    return callContext
    })().finally(()=>{this.outboundAttempt=null;this.outboundTarget=null})
    return this.outboundAttempt
  }

  connect(contactId: string) { return this.connectAttempt(config.endpoints.voiceCallContext,{contactId}) }
  quickDial(phone:string,prospectTaskId?:string){return this.connectAttempt(config.endpoints.voiceQuickDial,{phone,...(prospectTaskId?{prospectTaskId}:{})})}

  answer(id?: string) {
    const targetId = id || [...this.sessions.values()].find(session => session.state === 'ringing')?.id
    const call = targetId ? this.calls.get(targetId) : null
    if (!call || !targetId || this.activeCallId) return Promise.reject(new Error('voice_call_unavailable'))
    return new Promise<void>((resolve, reject) => {
      call.once('accept', resolve)
      call.once('cancel', () => reject(new Error('voice_call_canceled')))
      call.once('disconnect', () => reject(new Error('voice_call_disconnected')))
      call.accept()
    })
  }
  reject(id?: string) {
    const targetId = id || [...this.sessions.values()].find(session => session.state === 'ringing')?.id
    const call = targetId ? this.calls.get(targetId) : null
    call?.reject()
  }
  hangup() { this.clearStatusPolling(); const id = this.activeCallId; const call = id ? this.calls.get(id) : null; call?.disconnect(); if (id) this.removeCall(id); this.setState(this.incomingCallPending ? 'ringing' : 'ended') }
  toggleMute() { const id = this.activeCallId; const call = id ? this.calls.get(id) : null; if (call && id) { const muted = !call.isMuted(); call.mute(muted); this.updateSession(id, { muted }) } }

  diagnostics(): VoiceDiagnostics {
    return {
      deviceExists: Boolean(this.device),
      deviceState: this.device?.state ?? 'absent',
      identity: this.identity,
      tokenExpiresAt: this.tokenExpiresAt ? new Date(this.tokenExpiresAt).toISOString() : null,
      tokenExpired: this.tokenExpiresAt ? this.tokenExpiresAt <= Date.now() : null,
      incomingListenerAttached: this.incomingListenerAttached,
      signalingActive: this.device?.state === 'registered',
      edge: this.device?.edge ?? null,
      lastTokenInstalledAt: this.lastTokenInstalledAt,
      lastRegisteredAt: this.lastRegisteredAt,
      incomingFrom: this.incomingFrom,
      incomingCallPending: this.incomingCallPending,
      disposed: this.disposed,
      lastRegistrationErrorCode: this.lastRegistrationErrorCode,
    }
  }

  destroy() {
    this.disposed = true
    this.generation += 1
    this.clearStatusPolling()
    for (const call of this.calls.values()) call.disconnect()
    const device = this.device
    this.calls.clear()
    this.sessions.clear()
    this.activeCallId = null
    this.incomingFrom = null
    this.incomingCallPending = false
    this.device = null
    this.identity = null
    this.registration = null
    this.tokenRefresh = null
    this.incomingListenerAttached = false
    device?.destroy()
  }
}
